import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { FileText, AlertCircle, CheckSquare, TrendingUp } from 'lucide-react';
import Chart from 'react-apexcharts';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PendingTodosWidget from '../../components/Dashboard/PendingTodosWidget';
import OverdueTodosWidget from '../../components/Dashboard/OverdueTodosWidget';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    fetchDashboardData();
    fetchTrends();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/reports/dashboard');
      const data = response.data.data;
      setStats(data);

      // Prepare chart data
      if (data.casesByType && data.casesByType.length > 0) {
        setChartData({
          options: {
            chart: {
              type: 'donut',
            },
            labels: data.casesByType.map((item) => item._id),
            legend: {
              position: 'bottom',
            },
          },
          series: data.casesByType.map((item) => item.count),
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      // Fetch both case and task trends in parallel
      const [caseResponse, taskResponse] = await Promise.all([
        api.get('/reports/case-trends?days=30'),
        api.get('/reports/task-trends?days=30')
      ]);

      const caseData = caseResponse.data.data || {};
      const taskData = taskResponse.data.data || {};

      // Prepare Highcharts data with timestamps for datetime xAxis
      // Handle empty or undefined trends arrays
      const caseTrends = caseData.trends || [];
      const taskTrends = taskData.trends || [];

      const caseChartData = caseTrends.map(item => {
        const date = new Date(item.date);
        return [date.getTime(), item.count || 0];
      });

      const taskChartData = taskTrends.map(item => {
        const date = new Date(item.date);
        return [date.getTime(), item.count || 0];
      });
      
      // Calculate date range for xAxis from actual data points
      const now = Date.now();
      const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
      
      // Get all unique timestamps from both datasets (dates where cases or tasks exist)
      const allTimestamps = [
        ...new Set([
          ...caseChartData.map(point => point[0]),
          ...taskChartData.map(point => point[0])
        ])
      ].sort((a, b) => a - b);
      
      // Calculate min and max from actual data with padding
      let xAxisMin = thirtyDaysAgo;
      let xAxisMax = now;
      
      if (allTimestamps.length > 0) {
        const dataMin = Math.min(...allTimestamps);
        const dataMax = Math.max(...allTimestamps);
        // Add 1 day padding on each side to ensure first and last dates are visible
        const oneDay = 24 * 60 * 60 * 1000;
        xAxisMin = dataMin - oneDay;
        xAxisMax = dataMax + oneDay;
      }
      
      // Always set trendData, even if arrays are empty, so the chart renders
      setTrendData({
        chart: {
          type: 'line',
          height: 350,
          zoomType: 'x',
          panning: {
            enabled: true,
            type: 'x',
          },
          panKey: 'shift',
        },
        title: {
          text: 'Case & Task Trend Statistics',
          style: {
            fontSize: '18px',
            fontWeight: 'bold',
          },
        },
        subtitle: {
          text: caseChartData.length === 0 && taskChartData.length === 0 
            ? 'No data available for the selected period'
            : 'Click and drag to zoom in. Hold Shift and drag to pan.',
          style: {
            fontSize: '12px',
            color: '#666',
          },
        },
        xAxis: {
          type: 'datetime',
          title: {
            text: 'Date',
          },
          labels: {
            rotation: -45,
            style: {
              fontSize: '11px',
            },
            format: '{value:%b %d}',
          },
          min: xAxisMin,
          max: xAxisMax,
          startOnTick: true,
          endOnTick: true,
          // Force ticks at dates where data exists - this ensures dates with cases/tasks are always shown
          tickPositions: allTimestamps.length > 0 ? allTimestamps : undefined,
          dateTimeLabelFormats: {
            day: '%b %d',
            week: '%b %d',
            month: '%b %d',
            year: '%b %d'
          },
        },
        yAxis: {
          title: {
            text: 'Count',
          },
          min: 0,
        },
        tooltip: {
          shared: true,
          xDateFormat: '%b %d, %Y',
        },
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'top',
          layout: 'horizontal',
        },
        plotOptions: {
          line: {
            marker: {
              enabled: true,
              radius: 4,
            },
            lineWidth: 2,
          },
        },
        series: [
          {
            name: 'Cases',
            data: caseChartData,
            color: '#3B82F6',
          },
          {
            name: 'Tasks',
            data: taskChartData,
            color: '#10B981',
          },
        ],
        credits: {
          enabled: false,
        },
        navigation: {
          buttonOptions: {
            enabled: true,
            verticalAlign: 'top',
            y: -10,
          },
          menuItemStyle: {
            padding: '6px 14px',
          },
        },
      });
    } catch (error) {
      console.error('Error fetching trends:', error);
      // Set empty trend data on error so the chart section still renders
      setTrendData({
        chart: {
          type: 'line',
          height: 350,
        },
        title: {
          text: 'Case & Task Trend Statistics',
          style: {
            fontSize: '18px',
            fontWeight: 'bold',
          },
        },
        subtitle: {
          text: 'Unable to load trend data',
          style: {
            fontSize: '12px',
            color: '#666',
          },
        },
        xAxis: {
          type: 'datetime',
          title: {
            text: 'Date',
          },
        },
        yAxis: {
          title: {
            text: 'Count',
          },
          min: 0,
        },
        series: [
          {
            name: 'Cases',
            data: [],
            color: '#3B82F6',
          },
          {
            name: 'Tasks',
            data: [],
            color: '#10B981',
          },
        ],
        credits: {
          enabled: false,
        },
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome to the Case Management System</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Cases</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.cases?.total || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Open Flags</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.flags?.open || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Open Cases</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.cases?.open || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Urgent Cases</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.cases?.urgent || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Cases by Type</h2>
          {chartData ? (
            <Chart
              options={chartData.options}
              series={chartData.series}
              type="donut"
              height={300}
            />
          ) : (
            <p className="text-gray-500 text-center py-8">No data available</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Cases by Status</h2>
          {stats?.casesByStatus && stats.casesByStatus.length > 0 ? (
            <div className="space-y-4">
              {stats.casesByStatus.map((item) => (
                <div key={item._id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{item._id}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(item.count / stats.cases.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8 text-right">
                      {item.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No data available</p>
          )}
        </div>
      </div>

      {/* Combined Case & Task Trend Statistics Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        {trendData ? (
          <div>
            <div className="mb-2 flex justify-end gap-2">
              <button
                onClick={() => {
                  if (chartInstanceRef.current) {
                    chartInstanceRef.current.zoomOut();
                  }
                }}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                title="Zoom Out"
              >
                Zoom Out
              </button>
              <button
                onClick={() => {
                  if (chartInstanceRef.current) {
                    chartInstanceRef.current.xAxis[0].setExtremes(null, null);
                  }
                }}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                title="Reset Zoom"
              >
                Reset Zoom
              </button>
            </div>
            <HighchartsReact
              ref={chartRef}
              highcharts={Highcharts}
              options={trendData}
              callback={(chart) => {
                chartInstanceRef.current = chart;
              }}
            />
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading trend statistics...</p>
          </div>
        )}
      </div>

      {/* My Pending To-Dos Section */}
      <PendingTodosWidget />

      {/* My Overdue To-Dos Section */}
      <OverdueTodosWidget />

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/cases/create"
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-gray-900">Create New Case</span>
          </Link>
          <Link
            to="/flags"
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-gray-900">View Flagged Incidents</span>
          </Link>
          <Link
            to="/todos"
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CheckSquare className="w-5 h-5 text-green-600" />
            <span className="font-medium text-gray-900">View To-Do's</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

